import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    FlaskConical, Leaf, Boxes, TreePine, AlertTriangle, Droplet, Sprout,
    ArrowDownCircle, ArrowUpCircle,
} from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import {
    PH_SCALE, NUTRIENTS, GROW_MEDIA, SOIL_TYPES,
} from "@/lib/growLabData";

const TABS = [
    { id: "ph", Icon: FlaskConical, en: "pH", es: "pH", accent: "var(--lime)" },
    { id: "nutrients", Icon: Leaf, en: "Nutrients", es: "Nutrientes", accent: "var(--cyan)" },
    { id: "media", Icon: Boxes, en: "Indoor Media", es: "Sustratos Interior", accent: "var(--coral)" },
    { id: "soil", Icon: TreePine, en: "Outdoor Soil", es: "Suelo Exterior", accent: "var(--lime)" },
];

export default function GrowLab() {
    const { lang } = useLang();
    const [tab, setTab] = useState("ph");

    return (
        <main
            className="max-w-7xl mx-auto px-5 md:px-8 pt-12 md:pt-20 pb-24 relative"
            data-testid="grow-lab-page"
        >
            <div className="glow glow-lime -top-20 -left-20" />
            <div className="glow glow-cyan top-40 right-10" />

            <div className="relative z-10">
                <span className="tag">
                    {lang === "es" ? "// laboratorio de cultivo" : "// the grow lab"}
                </span>
                <h1 className="font-display font-black text-white text-5xl md:text-7xl mt-4 uppercase leading-[0.92]">
                    {lang === "es" ? "Sabes lo que" : "Know what"}{" "}
                    <span className="font-brush text-[var(--lime)] inline-block -rotate-3">
                        {lang === "es" ? "cultivas" : "feeds it"}
                    </span>
                </h1>
                <p className="font-mono text-[var(--muted)] mt-5 max-w-2xl">
                    {lang === "es"
                        ? "Una hoja de trampa para pH, nutrientes, sustratos interiores y tipos de suelo exterior. Inclínate sobre las pestañas."
                        : "Cheat sheet for pH, nutrients, indoor grow media, and outdoor soil types. Tap a tab below."}
                </p>

                {/* Tabs */}
                <div
                    className="mt-10 flex flex-wrap gap-2 border-b border-[var(--border)]"
                    data-testid="grow-lab-tabs"
                >
                    {TABS.map((tt) => {
                        const active = tab === tt.id;
                        return (
                            <button
                                key={tt.id}
                                onClick={() => setTab(tt.id)}
                                data-testid={`tab-${tt.id}`}
                                className={
                                    "font-mono uppercase text-xs tracking-[0.18em] px-4 py-3 flex items-center gap-2 border-b-2 -mb-px transition-colors " +
                                    (active
                                        ? "border-[var(--lime)] text-[var(--lime)]"
                                        : "border-transparent text-[var(--muted)] hover:text-white")
                                }
                                style={active ? { borderColor: tt.accent, color: tt.accent } : {}}
                            >
                                <tt.Icon size={14} />
                                {tt[lang] || tt.en}
                            </button>
                        );
                    })}
                </div>

                <div className="mt-10 relative">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={tab}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.22 }}
                        >
                            {tab === "ph" && <PHSection lang={lang} />}
                            {tab === "nutrients" && <NutrientsSection lang={lang} />}
                            {tab === "media" && <MediaSection lang={lang} />}
                            {tab === "soil" && <SoilSection lang={lang} />}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </main>
    );
}

function PHSection({ lang }) {
    const d = PH_SCALE[lang] || PH_SCALE.en;
    return (
        <section data-testid="section-ph">
            <h2 className="font-display font-black text-white text-3xl uppercase">
                {d.title}
            </h2>
            <p className="font-mono text-[var(--muted)] mt-3 max-w-3xl">{d.intro}</p>

            {/* pH scale visualization */}
            <div
                className="mt-8 border border-[var(--border)] overflow-hidden"
                data-testid="ph-scale"
            >
                <div className="flex">
                    {d.bands.map((b, i) => (
                        <div
                            key={i}
                            className="flex-1 p-3 text-center"
                            style={{ background: b.color, color: "#000" }}
                        >
                            <div className="font-display font-black text-xl">
                                {b.range}
                            </div>
                            <div className="font-mono text-[10px] uppercase tracking-widest mt-1">
                                {b.label}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="flex bg-black">
                    {d.bands.map((b, i) => (
                        <div
                            key={i}
                            className="flex-1 p-3 text-center font-mono text-[10px] text-[var(--muted)] leading-relaxed border-l border-[var(--border)] first:border-l-0"
                        >
                            {b.examples}
                        </div>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
                <div className="card p-6">
                    <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle size={16} className="text-[var(--coral)]" />
                        <h3 className="font-display font-bold uppercase text-sm tracking-widest text-[var(--coral)]">
                            {lang === "es" ? "Por qué importa" : "Why it matters"}
                        </h3>
                    </div>
                    <ul className="space-y-2">
                        {d.why.map((w, i) => (
                            <li
                                key={i}
                                className="font-mono text-sm text-[var(--text)] border-l-2 border-[var(--coral)] pl-3"
                            >
                                {w}
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="card p-6">
                    <div className="flex items-center gap-2 mb-3">
                        <Droplet size={16} className="text-[var(--lime)]" />
                        <h3 className="font-display font-bold uppercase text-sm tracking-widest text-[var(--lime)]">
                            {lang === "es" ? "Cómo ajustar" : "How to adjust"}
                        </h3>
                    </div>
                    <ul className="space-y-2">
                        {d.fix.map((f, i) => {
                            const isLower = /lower|bajar/i.test(f);
                            const isHigher = /raise|subir/i.test(f);
                            return (
                                <li
                                    key={i}
                                    className="font-mono text-sm text-[var(--text)] flex gap-2 items-start"
                                >
                                    {isLower ? (
                                        <ArrowDownCircle
                                            size={14}
                                            className="text-[var(--coral)] mt-0.5 shrink-0"
                                        />
                                    ) : isHigher ? (
                                        <ArrowUpCircle
                                            size={14}
                                            className="text-[var(--cyan)] mt-0.5 shrink-0"
                                        />
                                    ) : (
                                        <span className="text-[var(--lime)]">→</span>
                                    )}
                                    {f}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        </section>
    );
}

function NutrientsSection({ lang }) {
    const d = NUTRIENTS[lang] || NUTRIENTS.en;
    return (
        <section data-testid="section-nutrients">
            <h2 className="font-display font-black text-white text-3xl uppercase">
                {d.title}
            </h2>

            <h3 className="font-display font-bold text-[var(--cyan)] uppercase text-sm tracking-widest mt-8 mb-4">
                N · P · K — {lang === "es" ? "macronutrientes" : "the macros"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {d.macros.map((m, i) => (
                    <div
                        key={i}
                        className="card p-6 relative overflow-hidden"
                        data-testid={`macro-${m.symbol}`}
                    >
                        <span
                            className="absolute -top-6 -right-2 font-display font-black select-none pointer-events-none"
                            style={{
                                color: m.color,
                                fontSize: "8rem",
                                opacity: 0.12,
                                lineHeight: 1,
                            }}
                        >
                            {m.symbol}
                        </span>
                        <div
                            className="font-display font-black text-5xl"
                            style={{ color: m.color }}
                        >
                            {m.symbol}
                        </div>
                        <div className="font-display font-bold text-white text-xl mt-1">
                            {m.name}
                        </div>
                        <p className="font-mono text-sm text-[var(--text)] mt-3">
                            {m.role}
                        </p>
                        <div className="mt-4 space-y-1.5">
                            <Row
                                label={lang === "es" ? "Fuente" : "Source"}
                                value={m.source}
                            />
                            <Row
                                label={lang === "es" ? "Deficiencia" : "Deficiency"}
                                value={m.deficiency}
                                accent="var(--coral)"
                            />
                        </div>
                    </div>
                ))}
            </div>

            <h3 className="font-display font-bold text-[var(--coral)] uppercase text-sm tracking-widest mt-12 mb-4">
                {lang === "es" ? "Micronutrientes" : "Micronutrients"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {d.micros.map((m, i) => (
                    <div
                        key={i}
                        className="border border-[var(--border)] p-4 flex gap-3"
                        data-testid={`micro-${m.symbol}`}
                    >
                        <div className="font-display font-black text-2xl text-[var(--cyan)] w-12 shrink-0">
                            {m.symbol}
                        </div>
                        <div>
                            <div className="font-display font-bold text-white">
                                {m.name}
                            </div>
                            <p className="font-mono text-xs text-[var(--muted)] mt-1 leading-relaxed">
                                {m.note}
                            </p>
                        </div>
                    </div>
                ))}
            </div>

            <div className="terminal mt-10 p-5">
                <p className="font-mono text-[var(--lime)] text-sm">
                    <Sprout size={14} className="inline mr-2" />
                    {d.tip}
                </p>
            </div>
        </section>
    );
}

function MediaSection({ lang }) {
    const d = GROW_MEDIA[lang] || GROW_MEDIA.en;
    return (
        <section data-testid="section-media">
            <h2 className="font-display font-black text-white text-3xl uppercase">
                {d.title}
            </h2>
            <p className="font-mono text-[var(--muted)] mt-3 max-w-2xl">
                {d.subtitle}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
                {d.items.map((m, i) => (
                    <div
                        key={i}
                        className="card p-6 relative"
                        data-testid={`media-${i}`}
                    >
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <div className="font-display font-bold text-white text-lg leading-tight">
                                    {m.name}
                                </div>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mt-1">
                                    {lang === "es" ? "Ideal para" : "Best for"} → {m.best}
                                </div>
                            </div>
                            <div
                                className="w-10 h-10 border-2 border-black shrink-0"
                                style={{ background: m.color }}
                                aria-hidden
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-3">
                            <Cell label="pH" value={m.ph} />
                            <Cell
                                label={lang === "es" ? "Costo" : "Cost"}
                                value={m.cost}
                            />
                            <Cell
                                label={lang === "es" ? "Reusable" : "Reuse"}
                                value={m.reuse}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-4">
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--lime)] mb-1">
                                    {lang === "es" ? "Pros" : "Pros"}
                                </div>
                                <ul className="font-mono text-xs text-[var(--text)] space-y-0.5">
                                    {m.pros.map((p, j) => (
                                        <li key={j}>+ {p}</li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--coral)] mb-1">
                                    {lang === "es" ? "Contras" : "Cons"}
                                </div>
                                <ul className="font-mono text-xs text-[var(--text)] space-y-0.5">
                                    {m.cons.map((c, j) => (
                                        <li key={j}>− {c}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function SoilSection({ lang }) {
    const d = SOIL_TYPES[lang] || SOIL_TYPES.en;
    return (
        <section data-testid="section-soil">
            <h2 className="font-display font-black text-white text-3xl uppercase">
                {d.title}
            </h2>
            <p className="font-mono text-[var(--muted)] mt-3 max-w-2xl">
                {d.subtitle}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
                {d.items.map((s, i) => (
                    <div
                        key={i}
                        className="card p-5"
                        data-testid={`soil-${i}`}
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <div
                                className="w-12 h-12 border-2 border-black shrink-0"
                                style={{ background: s.color }}
                                aria-hidden
                            />
                            <div>
                                <div className="font-display font-bold text-white text-lg leading-tight">
                                    {s.name}
                                </div>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
                                    {s.feel}
                                </div>
                            </div>
                        </div>
                        <div className="space-y-1.5 mt-3">
                            <Row
                                label={lang === "es" ? "Drenaje" : "Drainage"}
                                value={s.drainage}
                            />
                            <Row
                                label={lang === "es" ? "Fertilidad" : "Fertility"}
                                value={s.fertility}
                            />
                            <Row label="pH" value={s.ph} />
                            <Row
                                label={lang === "es" ? "Mejor para" : "Best for"}
                                value={s.best}
                                accent="var(--lime)"
                            />
                            <Row
                                label={lang === "es" ? "Mejorar con" : "Fix with"}
                                value={s.fix}
                                accent="var(--cyan)"
                            />
                        </div>
                    </div>
                ))}
            </div>
            <div className="terminal mt-10 p-5 border-[var(--coral)]" style={{ boxShadow: "0 0 24px rgba(255, 51, 102, 0.18)" }}>
                <p className="font-mono text-[var(--coral)] text-sm flex gap-2">
                    <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                    <span>{d.urbanNote}</span>
                </p>
            </div>
        </section>
    );
}

function Row({ label, value, accent = "var(--cyan)" }) {
    return (
        <div className="flex gap-2 items-baseline">
            <span
                className="font-mono text-[10px] uppercase tracking-[0.22em] w-20 shrink-0"
                style={{ color: accent }}
            >
                {label}
            </span>
            <span className="font-mono text-xs text-[var(--text)] leading-snug">
                {value}
            </span>
        </div>
    );
}

function Cell({ label, value }) {
    return (
        <div className="border border-[var(--border)] p-2">
            <div className="font-mono text-[9px] uppercase tracking-widest text-[var(--muted)]">
                {label}
            </div>
            <div className="font-mono text-xs text-white mt-0.5">{value}</div>
        </div>
    );
}
